import { styled } from '@uhg-abyss/web/tools/styled';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Text } from '@uhg-abyss/web/ui/Text';

import { StyledDiv } from '../ConstantsStyles';
import { LinkWithTracking } from '../LinkWithTracking/LinkWithTracking';
import { AdditionalResourcesType } from './additionalResourcesType';

const CardContainer = styled('div', {
  height: '100%',
  textAlign: 'left',
  '.abyss-card-root': {
    borderRadius: '12px',
    border: '1px solid #E5E5E6',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.12)',
    minHeight: '124px',
    '@media (max-width: 984px)': {
      minHeight: 'unset',
    },
    '@media (min-width: 985px)': {
      maxWidth: '696px',
    },
    height: 'calc(100% - 32px)',
    '@media (max-width: 744px)': {
      fontSize: '12.64px',
      marginTop: '5px',
      lineHeight: '16px',
      minHeight: '82px',
    },
  },
  '.box-container': {
    padding: '0',
  },
});

const BoxContainer = styled('div', {
  height: '100%',
  '@media (max-width: 744px)': {
    height: 'unset',
  },
  '.abyss-box-root': {
    height: '100%',
    backgroundColor: 'transparent',
    padding: '0',
    display: 'flex',
    justifyContent: 'center',
  },
});
const ContentContainer = styled('div', {
  display: 'flex',
  height: '100%',
  '@media (max-width: 744px)': {
    width: 'unset',
    alignItems: 'flex-start',
  },
});
const TextContainer = styled('div', {
  '@media (max-width: 744px)': {
    maxWidth: '380px',
  },
});

export const LinkContainer = styled('div', {
  display: 'flex',
  height: '30px',
  zIndex: '2',
  padding: '8px 0px 0px 0px',
});

type Props = {
  resource: AdditionalResourcesType;
};

export const AdditionalResourcesCard = ({ resource }: Props) => (
  <CardContainer>
    <BoxContainer>
      <Card
        css={{
          width: '406px',
          height: '100%',
          '@media (max-width: 464px)': {
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            alignSelf: 'stretch',
          },
        }}
      >
        <StyledDiv
          css={{
            padding: '8px 16px 8px 16px',
            '@media (max-width: 744px)': {
              padding: '8px 16px ',
            },
          }}
        >
          <Flex
            css={{
              height: '100%',
            }}
            justify="space-between"
          >
            <Heading
              color="$core.color.brand.100"
              css={{
                '&.abyss-heading-root': {
                  textAlign: 'left',
                  fontSize: 'medium',
                  lineHeight: '$lg',
                  '@media (max-width: 744px)': {
                    fontSize: '16px',
                    lineHeight: '$md !important',
                  },

                  color: '#323333 !important',
                },
              }}
              data-auto-testid="additional-resource-card-heading"
              data-testid="additional-resource-card-heading"
              level={3}
              size="xs"
            >
              {resource?.title}
            </Heading>
            <ContentContainer>
              <TextContainer>
                <Text
                  color="$core.color.neutral.80"
                  css={{
                    'abyss-text-root': {
                      color: '#222',
                      '@media (max-width: 744px)': {
                        fontSize: '14.22px',
                        lineHeight: '20px',
                      },
                    },
                  }}
                  data-auto-testid="additional-resource-card-description"
                  data-testid="additional-resource-card-description"
                >
                  {resource?.description}
                </Text>
              </TextContainer>
            </ContentContainer>

            <LinkContainer>
              <LinkWithTracking
                after={
                  resource?.linkText && resource?.linkUrl ? (
                    <IconSymbol
                      icon="open_in_new"
                      size="18px"
                      style={{ marginTop: '1px' }}
                    />
                  ) : undefined
                }
                analyticsInfo={{
                  location: 'body:additional resources',
                  destinationUrl: resource?.linkUrl,
                }}
                aria-label={`${resource?.linkText} ${resource?.description}`}
                css={{
                  '.abyss-insert-element-root': {
                    '@media (max-width: 464px)': {
                      alignItems: 'center',
                    },
                  },
                  '.abyss-insert-element-after': {
                    '@media (max-width: 464px)': {
                      alignItems: 'center',
                      marginLeft: '0px !important',
                    },
                  },
                }}
                data-auto-testid={`my-benefits-${resource?.title}`}
                data-testid={`my-benefits-${resource?.title
                  ?.replace(/ /g, '-')
                  ?.toLowerCase()}`}
                hideIcon
                href={resource?.linkUrl}
                isStandardAnchor={false}
              >
                <Text
                  css={{
                    'abyss-text-root': {
                      color: '$core.color.brand.60',
                      '@media (min-width: 465px)': {
                        fontWeight: 'bold',
                        fontSize: '16px',
                        display: 'flex',
                        height: '26px',
                        justifyContent: 'center',
                        width: 'auto',
                        alignItems: 'center',
                        gap: '4px',
                      },
                      '@media (max-width: 464px)': {
                        display: 'flex',
                        height: '18px',
                        fontSize: '12.64px',
                        fontWeight: 'bold',
                        width: 'auto',
                        alignItems: 'center',
                        gap: '4px',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      },
                    },
                  }}
                >
                  {resource?.linkText}
                </Text>
              </LinkWithTracking>
            </LinkContainer>
          </Flex>
        </StyledDiv>
      </Card>
    </BoxContainer>
  </CardContainer>
);
